import { useEffect, useMemo, useState } from 'react';
import {
  cancelSubscription,
  getSubscriptionPlans,
  getSubscriptions,
  subscribe,
} from 'src/api/subscription';
import { Subscription, SubscriptionDetails } from 'src/models/subscription';
import { SubscriptionPlan } from 'src/models/subscription-plans';
import { SubscriptionContext, SubscriptionResult } from './subscription-context-types';

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [subResult, setSubResult] = useState<SubscriptionResult>();
  const [nextBillingAmount, setNextBillingAmount] = useState<number>(0);

  useEffect(() => {
    getSubscriptionPlans().then((res) => {
      if (res.success) {
        setSubscriptionPlans(res.data);
      }
    });
  }, []);

  const providerValue = useMemo(() => {
    const getSubscriptionDetails = (organization: string) => {
      getSubscriptions(organization).then((res) => {
        if (res.success) {
          setSubscriptions(res.data);
        }
      });
    };

    const subscribeSubscription = async (
      id: string,
      data: Partial<SubscriptionDetails>
    ): Promise<boolean> => {
      const res = await subscribe(id, data);
      if (res.success) {
        setSubResult(res.data as SubscriptionResult);
      }
      return res.success;
    };

    const updateNextBillingAmount = (amount: number, users: number) => {
      setNextBillingAmount(amount * users);
    };

    const cancelSubscriptionPlan = (id: string): void => {
      cancelSubscription(id).then((res) => {
        if (res.success) {
          setSubscriptions((prev) =>
            prev.map((sub) =>
              sub.subscription.stripeSubscriptionId === id
                ? { ...sub, subscription: { ...sub.subscription, status: 'canceled' } }
                : sub
            )
          );
        }
      });
    };

    return {
      subscriptionPlans,
      subscribeSubscription,
      subResult,
      setSubscriptionPlans,
      subscriptions,
      setSubscriptions,
      updateNextBillingAmount,
      nextBillingAmount,
      cancelSubscriptionPlan,
      getSubscriptionDetails,
    };
  }, [subscriptionPlans, subResult, subscriptions, nextBillingAmount]);

  return (
    <SubscriptionContext.Provider value={providerValue}>{children}</SubscriptionContext.Provider>
  );
};

export default SubscriptionProvider;
